import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { OccasionalSlot } from '@/types/Slots'
import { Daycare } from '@/types/Daycare'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './unbookOccasionalSlot.module.scss'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { UNBOOK } from '@/modules/API/slot/bookingHandler'
import { useMutation } from '@apollo/client'
import moment from 'moment/moment'
import { alertRangeToNumber } from '@/modules/helper/daycareHelper'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Check, Close } from '@mui/icons-material'
import TextArea from '@/components/_custom/TextArea/textArea'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface UnbookOccasionalSlotProps {
    open: boolean
    handleClose: () => void
    occasionalSlots: OccasionalSlot[]
    daycare: Daycare
    refetchData: () => void
    closeParentDialog: () => void
}

const UnbookOccasionalSlot: FunctionComponent<UnbookOccasionalSlotProps> = ({
    open,
    handleClose,
    occasionalSlots,
    daycare,
    refetchData,
    closeParentDialog
}: UnbookOccasionalSlotProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [parentComment, setParentComment] = useState('')

    const [selectChilds, setSelectChilds] = useState<string[]>(
        occasionalSlots.map((slot) => {
            return slot.id.toString()
        })
    )

    const selectOptionsChild: SelectOptionsDeprecated = [
        {
            options: occasionalSlots.map((slot: OccasionalSlot): SelectOptionDeprecated => {
                return {
                    name: slot.childName + (slot.childComplement ? ' - ' + slot.childComplement : ''),
                    value: slot.id.toString()
                }
            })
        }
    ]

    const [cancelOccasionalSlot, { loading: loadingUnbook }] = useMutation(UNBOOK, {
        onCompleted: () => {
            refetchData()
            closeParentDialog()

            showNotificationSuccess(t('unbookSuccess'))
        },
        onError: () => {
            showNotificationError(t('unbookError'))
        }
    })

    function cancelAllOccasionalSlot(): void {
        occasionalSlots
            .filter((slot: OccasionalSlot): boolean => selectChilds.includes(slot.id.toString()))
            .forEach((slot: OccasionalSlot) => {
                if (daycare.merger) {
                    cancelOccasionalSlot({
                        variables: {
                            date: slot.date,
                            start: slot.start,
                            end: slot.end,
                            child_id: slot.childId,
                            daycare_id: daycare.id,
                            parent_comment: parentComment ? parentComment : null
                        }
                    })
                } else {
                    cancelOccasionalSlot({
                        variables: {
                            occasional_slot_id: slot.id,
                            child_id: slot.childId,
                            daycare_id: daycare.id,
                            parent_comment: parentComment ? parentComment : null
                        }
                    })
                }
            })
    }

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('unbookOccasionalSlot')} parent />
            <DialogBody className={styles.DialogBody}>
                {daycare.alertComment &&
                    moment(occasionalSlots[0].date, 'DD-MM-YYYY')
                        .subtract(alertRangeToNumber(daycare), 'hours')
                        .isSameOrBefore(moment()) && <p className={styles.Text}>{daycare.alertComment}</p>}
                {occasionalSlots.length > 1 && (
                    <SelectMultiple
                        selectOptions={selectOptionsChild}
                        selectValues={selectChilds}
                        setSelectValues={setSelectChilds}
                        className={styles.UserInput}
                        isParent
                    />
                )}
                <div className={styles.TextAreaInput}>
                    <p className={styles.TextAreaTitle}>{t('unbookReason')}</p>
                    <TextArea
                        placeholder={t('unbookReason')}
                        maxCount={200}
                        className={styles.UserInput}
                        parent
                        value={parentComment}
                        onChange={setParentComment}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('yes')}
                        customIcon={<Check />}
                        onClick={cancelAllOccasionalSlot}
                        className={styles.Button}
                        parent
                        disabled={loadingUnbook}
                    />
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('no')}
                        customIcon={<Close />}
                        onClick={handleClose}
                        className={styles.Button}
                        parent
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default UnbookOccasionalSlot
